import { useState, useEffect, useCallback, type FormEvent } from "react";
import {
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton as ModalClose,
  FormControl, FormLabel, Input, Select, Switch,
  VStack, HStack, Box, Flex, Text, Badge, Button, Icon, IconButton, Spinner,
  useColorModeValue, useToast,
} from "@chakra-ui/react";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { GripVertical, Plus, Pencil, Trash2, X } from "lucide-react";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { settings } from "../../../shared/settings";
import type { FormField, FormType, FieldType, FieldOption } from "./types";

const base = () => settings.environment.VITE_PROCESS_HUB_BASE_URL ?? "";

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Texto",
  textarea: "Texto longo",
  number: "Número",
  date: "Data",
  select: "Seleção única",
  multiselect: "Seleção múltipla",
  boolean: "Sim/Não",
  email: "E-mail",
  phone: "Telefone",
  radio: "Seleção única (radio)",
  currency: "Moeda",
  file: "Arquivo",
};

const NEEDS_OPTIONS: FieldType[] = ["select", "multiselect", "radio"];

// ── FormFieldModal (componente privado) ───────────────────────────────────────

interface FieldFormState {
  key: string;
  label: string;
  field_type: FieldType;
  is_required: boolean;
  placeholder: string;
  options: FieldOption[];
}

const DEFAULT_FIELD: FieldFormState = {
  key: "",
  label: "",
  field_type: "text",
  is_required: false,
  placeholder: "",
  options: [],
};

interface FormFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  formTypeId: number;
  field: FormField | null;
  onSuccess: () => void;
}

function FormFieldModal({ isOpen, onClose, formTypeId, field, onSuccess }: FormFieldModalProps) {
  const toast = useToast();
  const { request } = useApiRequest();

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const labelColor  = useColorModeValue("#434347", "#e8edf2");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const inputBg     = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const optionsBg   = useColorModeValue("rgba(0,0,0,0.02)", "rgba(255,255,255,0.02)");

  const [data, setData] = useState<FieldFormState>(DEFAULT_FIELD);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (field) {
      setData({
        key: field.key,
        label: field.label,
        field_type: field.field_type,
        is_required: field.is_required,
        placeholder: field.placeholder ?? "",
        options: field.options ?? [],
      });
    } else {
      setData(DEFAULT_FIELD);
    }
  }, [isOpen, field]);

  const addOption = () =>
    setData((d) => ({ ...d, options: [...d.options, { value: "", label: "" }] }));

  const updateOption = (idx: number, key: keyof FieldOption, value: string) => {
    const opts = [...data.options];
    opts[idx] = { ...opts[idx], [key]: value };
    setData((d) => ({ ...d, options: opts }));
  };

  const removeOption = (idx: number) =>
    setData((d) => ({ ...d, options: d.options.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!data.label.trim()) {
      toast({ title: "O label é obrigatório", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (!field && !data.key.trim()) {
      toast({ title: "A chave é obrigatória", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    try {
      setSubmitting(true);
      if (field) {
        await request({
          url: `${base()}/api/v1/forms/${formTypeId}/fields/${field.id}`,
          method: "PATCH",
          payload: {
            label: data.label,
            is_required: data.is_required,
            placeholder: data.placeholder || null,
            options: NEEDS_OPTIONS.includes(data.field_type) ? data.options : null,
          },
            skipToast: true,
        });
      } else {
        await request({
          url: `${base()}/api/v1/forms/${formTypeId}/fields`,
          method: "POST",
          payload: {
            key: data.key,
            label: data.label,
            field_type: data.field_type,
            is_required: data.is_required,
            placeholder: data.placeholder || null,
            options: NEEDS_OPTIONS.includes(data.field_type) ? data.options : null,
          },
            skipToast: true,
        });
      }
      toast({
        title: field ? "Campo atualizado" : "Campo adicionado",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSuccess();
      onClose();
    } catch {
      toast({ title: "Erro ao salvar campo", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit} borderRadius="2xl" mx={4}>
        <ModalHeader
          fontSize="lg"
          fontWeight={600}
          color={titleColor}
          borderBottomWidth="1px"
          borderColor={cardBorder}
          pb={4}
        >
          {field ? "Editar Campo" : "Novo Campo"}
        </ModalHeader>
        <ModalClose />

        <ModalBody pt={5} maxH="70vh" overflowY="auto">
          <VStack spacing={4} align="stretch">
            {!field && (
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Chave</FormLabel>
                <Input
                  value={data.key}
                  onChange={(e) => setData((d) => ({ ...d, key: e.target.value }))}
                  placeholder="ex: nome_completo"
                  fontFamily="mono"
                  bg={inputBg}
                  borderColor={inputBorder}
                  borderRadius="8px"
                  fontSize="sm"
                  _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
                />
              </FormControl>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Label</FormLabel>
              <Input
                value={data.label}
                onChange={(e) => setData((d) => ({ ...d, label: e.target.value }))}
                placeholder="ex: Nome Completo"
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Tipo do campo</FormLabel>
              {field ? (
                <Box
                  px={3}
                  py={2}
                  borderRadius="8px"
                  borderWidth="1px"
                  borderColor={inputBorder}
                  bg={inputBg}
                  fontSize="sm"
                  color={labelColor}
                >
                  {FIELD_TYPE_LABELS[field.field_type]}
                </Box>
              ) : (
                <Select
                  value={data.field_type}
                  onChange={(e) =>
                    setData((d) => ({ ...d, field_type: e.target.value as FieldType, options: [] }))
                  }
                  bg={inputBg}
                  borderColor={inputBorder}
                  borderRadius="8px"
                  fontSize="sm"
                  _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
                >
                  {(Object.keys(FIELD_TYPE_LABELS) as FieldType[]).map((type) => (
                    <option key={type} value={type}>{FIELD_TYPE_LABELS[type]}</option>
                  ))}
                </Select>
              )}
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Obrigatório</FormLabel>
              <Flex align="center" gap={3} h="40px">
                <Switch
                  colorScheme="blue"
                  isChecked={data.is_required}
                  onChange={(e) => setData((d) => ({ ...d, is_required: e.target.checked }))}
                />
                <Text fontSize="sm" color={labelColor}>{data.is_required ? "Sim" : "Não"}</Text>
              </Flex>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Placeholder</FormLabel>
              <Input
                value={data.placeholder}
                onChange={(e) => setData((d) => ({ ...d, placeholder: e.target.value }))}
                placeholder="Texto de apoio para o preenchimento..."
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            {NEEDS_OPTIONS.includes(data.field_type) && (
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="sm" fontWeight={600} color={labelColor}>Opções</Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={<Icon as={Plus} boxSize={3.5} />}
                    onClick={addOption}
                  >
                    Adicionar
                  </Button>
                </Flex>
                <VStack
                  spacing={2}
                  p={3}
                  bg={optionsBg}
                  borderRadius="8px"
                  borderWidth="1px"
                  borderColor={inputBorder}
                >
                  {data.options.length === 0 ? (
                    <Text fontSize="xs" color="gray.400" py={1}>Nenhuma opção ainda.</Text>
                  ) : (
                    data.options.map((opt, idx) => (
                      <HStack key={idx} w="full" spacing={2}>
                        <Input
                          size="sm"
                          fontFamily="mono"
                          placeholder="valor"
                          value={opt.value}
                          onChange={(e) => updateOption(idx, "value", e.target.value)}
                        />
                        <Input
                          size="sm"
                          placeholder="texto visível"
                          value={opt.label}
                          onChange={(e) => updateOption(idx, "label", e.target.value)}
                        />
                        <IconButton
                          aria-label="Remover opção"
                          icon={<Icon as={X} boxSize={3.5} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          flexShrink={0}
                          onClick={() => removeOption(idx)}
                        />
                      </HStack>
                    ))
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
          <Button variant="ghost" borderRadius="50px" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            size="sm"
            type="submit"
            isLoading={submitting}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── FormFieldsDrawer ──────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  formType: FormType | null;
}

export function FormFieldsDrawer({ isOpen, onClose, formType }: Props) {
  const toast = useToast();
  const { request } = useApiRequest();

  const [fields, setFields] = useState<FormField[]>([]);
  const [loading, setLoading] = useState(false);
  const [fieldModal, setFieldModal] = useState<{ isOpen: boolean; field: FormField | null }>({
    isOpen: false,
    field: null,
  });

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const subtleColor = useColorModeValue("#84858c", "#718096");
  const headerBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)");
  const drawerBg    = useColorModeValue("white", "#0f1f3a");
  const rowBg       = useColorModeValue("white", "#0f2040");
  const rowBorder   = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)");
  const gripColor   = useColorModeValue("#c0c0c8", "#4a5568");

  const fetchFields = useCallback(async () => {
    if (!formType) return;
    try {
      setLoading(true);
      const data = await request({
        url: `${base()}/api/v1/forms/${formType.id}`,
        method: "GET",
        skipToast: true,
      });
      setFields([...data.fields].sort((a: FormField, b: FormField) => a.order - b.order));
    } catch {
      toast({ title: "Erro ao carregar campos", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [formType, request, toast]);

  useEffect(() => {
    if (isOpen && formType) fetchFields();
  }, [isOpen, formType, fetchFields]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index || !formType) return;

    const reordered = Array.from(fields);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const withOrder = reordered.map((f, i) => ({ ...f, order: i + 1 }));
    setFields(withOrder);

    try {
      await request({
        url: `${base()}/api/v1/forms/${formType.id}/fields/reorder`,
        method: "PUT",
        payload: { fields: withOrder.map((f) => ({ id: f.id, order: f.order })) },
        skipToast: true,
      });
    } catch {
      toast({ title: "Erro ao reordenar campos", status: "error", duration: 4000, isClosable: true });
      fetchFields();
    }
  };

  const handleRemove = async (field: FormField) => {
    if (!formType) return;
    try {
      await request({
        url: `${base()}/api/v1/forms/${formType.id}/fields/${field.id}`,
        method: "DELETE",
        skipToast: true,
      });
      setFields((prev) => prev.filter((f) => f.id !== field.id));
      toast({ title: "Campo removido", status: "success", duration: 3000, isClosable: true });
    } catch {
      toast({ title: "Erro ao remover campo", status: "error", duration: 4000, isClosable: true });
    }
  };

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent bg={drawerBg}>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor={headerBorder} pb={4}>
            <Flex justify="space-between" align="center" pr={8}>
              <Box>
                <Text fontSize="md" fontWeight={600} color={titleColor} lineHeight="1.2">
                  {formType?.name}
                </Text>
                <Text fontSize="xs" color={subtleColor}>Campos do formulário</Text>
              </Box>
              <Button
                leftIcon={<Icon as={Plus} boxSize={4} />}
                bg="#009efb"
                color="white"
                _hover={{ bg: "#007bd4" }}
                borderRadius="50px"
                size="sm"
                onClick={() => setFieldModal({ isOpen: true, field: null })}
              >
                Adicionar campo
              </Button>
            </Flex>
          </DrawerHeader>

          <DrawerBody pt={5}>
            {loading ? (
              <Flex justify="center" align="center" py={16}>
                <Spinner size="md" color="#009efb" />
              </Flex>
            ) : fields.length === 0 ? (
              <Flex
                align="center"
                justify="center"
                py={10}
                borderRadius="lg"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor={rowBorder}
              >
                <Text fontSize="sm" color={subtleColor}>Nenhum campo cadastrado.</Text>
              </Flex>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="form-fields">
                  {(provided) => (
                    <Box {...provided.droppableProps} ref={provided.innerRef}>
                      {fields.map((field, index) => (
                        <Draggable key={field.id} draggableId={String(field.id)} index={index}>
                          {(provided, snapshot) => (
                            <Flex
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              align="center"
                              gap={3}
                              px={3}
                              py={2.5}
                              mb={2}
                              bg={rowBg}
                              borderRadius="lg"
                              borderWidth="1px"
                              borderColor={rowBorder}
                              boxShadow={snapshot.isDragging ? "md" : "none"}
                              transition="box-shadow 0.15s"
                            >
                              <Box
                                {...provided.dragHandleProps}
                                color={gripColor}
                                cursor="grab"
                                _active={{ cursor: "grabbing" }}
                                flexShrink={0}
                              >
                                <Icon as={GripVertical} boxSize={4} />
                              </Box>

                              <Text
                                fontSize="xs"
                                color={subtleColor}
                                w="18px"
                                textAlign="right"
                                flexShrink={0}
                              >
                                {index + 1}
                              </Text>

                              <Box flex={1} minW={0}>
                                <Text fontSize="sm" fontWeight={500} color={titleColor} noOfLines={1}>
                                  {field.label}
                                </Text>
                                <Text fontSize="xs" color={subtleColor} fontFamily="mono" noOfLines={1}>
                                  {field.key}
                                </Text>
                              </Box>

                              <HStack spacing={1.5} flexShrink={0}>
                                <Badge
                                  borderRadius="50px"
                                  bg="rgba(0,158,251,0.12)"
                                  color="#009efb"
                                  fontSize="11px"
                                  fontWeight={600}
                                  px={2.5}
                                  py={0.5}
                                >
                                  {FIELD_TYPE_LABELS[field.field_type]}
                                </Badge>
                                {field.is_required && (
                                  <Badge
                                    borderRadius="50px"
                                    colorScheme="orange"
                                    variant="subtle"
                                    fontSize="11px"
                                    px={2}
                                    py={0.5}
                                  >
                                    Obrig.
                                  </Badge>
                                )}
                              </HStack>

                              <HStack spacing={0} flexShrink={0}>
                                <IconButton
                                  aria-label="Editar campo"
                                  icon={<Icon as={Pencil} boxSize={3.5} />}
                                  size="sm"
                                  variant="ghost"
                                  color="#009efb"
                                  _hover={{ bg: "rgba(0,158,251,0.08)" }}
                                  onClick={() => setFieldModal({ isOpen: true, field })}
                                />
                                <IconButton
                                  aria-label="Remover campo"
                                  icon={<Icon as={Trash2} boxSize={3.5} />}
                                  size="sm"
                                  variant="ghost"
                                  color={subtleColor}
                                  _hover={{ bg: "rgba(220,53,69,0.08)", color: "#dc3545" }}
                                  onClick={() => handleRemove(field)}
                                />
                              </HStack>
                            </Flex>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {formType && (
        <FormFieldModal
          isOpen={fieldModal.isOpen}
          onClose={() => setFieldModal({ isOpen: false, field: null })}
          formTypeId={formType.id}
          field={fieldModal.field}
          onSuccess={fetchFields}
        />
      )}
    </>
  );
}
